<template>
  <v-container
    fluid
    class="entity-edit-form"
    :class="{ 'with-floating-buttons': showFloatingButtons }"
  >
    <breadcrumbs />
    <v-row>
      <v-col>
        <h1 class="mb-1">
          {{ title }}
        </h1>
      </v-col>
      <v-col v-if="canEdit" class="text-right">
        <slot :name="'before-create-btn'" />
        <v-btn
          depressed
          color="success"
          large
          @click="goToCreate"
        >
          <v-icon left v-text="`mdi-plus`" />
          Создать
        </v-btn>
        <slot :name="'after-create-btn'" />
      </v-col>
    </v-row>

    <!-- Плашка фильтров между заголовком страницы и таблицей -->
    <slot name="top-filter" />

    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Поиск"
              outlined
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>

          <slot name="filter" :filter="filter"></slot>

          <loader v-if="loading" />

          <v-data-table
            v-else
            fixed-header
            :search="search"
            :headers="allTableHeaders"
            :items="visibleItems"
            :items-per-page="-1"
            class="elevation-1 menu-list"
          >
            <template
              v-for="(row, key) in headers"
              v-slot:[`item.${row.value}`]="{ item }"
            >
              <slot :name="`item.${row.value}`" :item="item">
                <template v-if="row.type === Boolean">
                  <span
                    v-if="item[row.value]"
                    :key="key"
                    class="color-green"
                  ><font-awesome-icon
                    icon="check"
                  /></span>
                  <span
                    v-else
                    :key="key"
                    class="color-red"
                  ><font-awesome-icon
                    icon="times"
                  /></span>
                </template>
                <template v-else-if="row.type === 'boolean-check'">
                  <span
                    v-if="item[row.value]"
                    :key="key"
                    class="color-green"
                  ><font-awesome-icon
                    icon="check"
                  /></span>
                </template>
                <template v-else-if="row.type === 'price'">
                  {{ item[row.value] | amountStringWithCurrency }}
                </template>
                <template v-else-if="row.type === 'relation'">
                  <span v-if="item[row.value]" :key="key">{{
                    item[row.value][row.field || 'name']
                  }}</span>
                </template>
                <template v-else>
                  {{ item[row.value] }}
                </template>
              </slot>
            </template>

            <!-- eslint-disable-next-line -->
            <template #item.status="{ item }">
              <div
                v-if="allowToggleStatus && canEdit"
                class="d-inline-flex justify-center"
              >
                <v-switch
                  :input-value="item.status"
                  readonly
                  @click="handleStatusChange(item.id)"
                ></v-switch>
              </div>
              <template v-else>
                <span
                  v-if="item.status"
                  class="color-green"
                ><font-awesome-icon
                  icon="check"
                /></span>
                <span
                  v-else
                  class="color-red"
                ><font-awesome-icon
                  icon="times"
                /></span>
              </template>
            </template>
            <!-- eslint-disable-next-line -->
            <template v-if="canEdit" v-slot:item.actions="{ item }">
              <div class="d-flex align-center justify-end">
                <slot name="extra-actions" :item="item" />
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <router-link
                      tag="a"
                      class="edit-item-btn d-xl-none mr-2 v-btn v-btn--flat v-btn--depressed v-btn--icon v-btn--round theme--light elevation-1 v-size--small primary"
                      v-bind="attrs"
                      :to="{ name: editRouteName, params: { id: item.id } }"
                      v-on="on"
                    >
                      <span class="v-btn__content">
                        <!-- eslint-disable-next-line -->
                        <v-icon small>mdi-pencil</v-icon>
                      </span>
                    </router-link>
                    <router-link
                      tag="a"
                      class="d-none d-xl-inline-flex mr-2 v-btn v-btn--contained v-btn--depressed theme--light v-size--small primary"
                      v-bind="attrs"
                      :to="{ name: editRouteName, params: { id: item.id } }"
                      v-on="on"
                    >
                      <span class="v-btn__content"> Изменить </span>
                    </router-link>
                  </template>
                  <span>Редактировать</span>
                </v-tooltip>
                <slot name="delete-action">
                  <template v-if="allowDelete">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          color="accent"
                          elevation="1"
                          icon
                          small
                          class="mb-2 mt-2"
                          v-bind="attrs"
                          v-on="on"
                          @click="openDeleteModal(item.id)"
                        >
                          <!-- eslint-disable-next-line -->
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Удалить</span>
                    </v-tooltip>
                  </template>
                </slot>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <confirm-dialog
        v-model="statusDialog"
        :title="`Вы действительно хотите сменить статус '${toggleStatusItem.name}'?`"
        :loading="toggling"
        @confirm="toggleStatus"
        @close="closeStatusDialog"
      />
      <v-dialog v-model="dialog" persistent max-width="700">
        <v-card>
          <v-card-title class="headline">
            Вы действительно хотите удалить "{{ deletingItem.name }}"?
          </v-card-title>
          <!--          <v-card-text>
            Эту операцию невозможно будет отменить, вы точно хотите удалить?
          </v-card-text>-->
          <v-card-actions>
            <v-spacer />
            <v-btn color="green darken-1" text @click="closeModal">
              Отмена
            </v-btn>
            <v-btn
              :loading="deleting"
              color="red darken-1"
              text
              @click="deleteItem"
            >
              Удалить
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
  import mixinSnackbarMessage from '@/mixins/snackbar-message'
  import Loader from '@/components/loader/Loader'
  import { RestApiDataService } from '@/services/api/RestApiDataService'
  import { fi } from 'date-fns/locale'
  import ConfirmDialog from '../dialogs/ConfirmDialog.vue'
  import permissions from '@/mixins/permissions'
  import { mapState } from 'vuex'

  export default {
    name: 'EntityListTable',
    components: {
      Loader,
      ConfirmDialog,
    },
    mixins: [permissions, mixinSnackbarMessage],
    props: {
      title: String,
      dataService: RestApiDataService,
      headers: Array,

      showStatusColumn: {
        type: Boolean,
        default: true,
      },
      showActionsColumn: {
        type: Boolean,
        default: true,
      },
      allowDelete: {
        type: Boolean,
        default: true,
      },
      allowToggleStatus: {
        type: Boolean,
        default: false,
      },
      editPermission: {
        type: String,
        default: null,
      },

      createRouteName: String,
      editRouteName: String,

      filter: {
        type: Object,
        default: () => {},
      },

      // Клиентская пост-фильтрация уже загруженных строк (item) => boolean.
      // null - показывать всё (поведение по умолчанию, как раньше).
      itemsFilterFn: {
        type: Function,
        default: null,
      },
    },
    data () {
      return {
        slotName: 'name',
        loading: false,
        items: [],
        deleting: false,
        search: '',
        deleteId: null,
        dialog: false,
        toggling: false,
        toggleId: null,
        statusDialog: false,
      }
    },
    computed: {
      ...mapState({
        showFloatingButtons: (state) => state.app.showFloatingButtons,
      }),
      fi () {
        return fi
      },
      canEdit () {
        return !this.editPermission || this.userCan(this.editPermission)
      },
      visibleItems () {
        return this.itemsFilterFn
          ? this.items.filter(this.itemsFilterFn)
          : this.items
      },
      allTableHeaders () {
        const headers = [...this.headers]

        if (this.showStatusColumn) {
          headers.push({
            text: 'Статус',
            value: 'status',
            align: 'center',
          })
        }

        if (this.showActionsColumn && this.canEdit) {
          headers.push({
            text: 'Действия',
            value: 'actions',
            align: 'end',
            sortable: false,
          })
        }

        return headers
      },
      deletingItem () {
        return this.items.find((item) => item.id === this.deleteId) ?? {}
      },
      toggleStatusItem () {
        return this.items.find((item) => item.id === this.toggleId) ?? {}
      },
    },
    watch: {
      filter () {
        console.info('setItemsFilter')
        console.log({
          filter: this.filter,
        })
        this.loadList()
      },
    },
    created () {
      this.loadList()
    },
    methods: {
      async loadList () {
        this.loading = true
        try {
          const filter = {
            ...this.filter,
            ...{
              limit: 100000,
              offset: 0,
              page: 1,
            },
          }
          this.items = await this.dataService.getList(filter)
        } catch (err) {
          this.showErrorFromResponse(err)
        }
        this.loading = false
      },
      handleStatusChange (id) {
        this.toggleId = id
        this.statusDialog = true
      },
      closeStatusDialog () {
        this.toggleId = null
        this.statusDialog = false
      },
      async toggleStatus () {
        this.toggling = true

        try {
        ;(await this.toggleStatusItem.status)
          ? this.dataService.disable(this.toggleId)
          : this.dataService.enable(this.toggleId)
        this.items = this.items.map((item) => {
          if (item.id === this.toggleId) {
            item.status = !this.toggleStatusItem.status
          }

          return item
        })
        } catch (err) {
          this.showErrorFromResponse(err)
        }

        this.closeStatusDialog()
        this.toggling = false
      },
      openDeleteModal (id) {
        this.deleteId = id
        this.dialog = true
      },
      closeModal () {
        this.deleteId = null
        this.dialog = false
      },

      async deleteItem () {
        this.deleting = true

        try {
          await this.dataService.delete(this.deleteId)
          this.items = this.items.filter((item) => item.id !== this.deleteId)
        } catch (err) {
          this.showErrorFromResponse(err)
        }

        this.closeModal()
        this.deleting = false
      },

      goToCreate () {
        this.$router.push({ name: this.createRouteName })
      },
      goToEdit (id) {
        this.$router.push({
          name: this.editRouteName,
          params: { id: id },
        })
      },
    },
  }
</script>

<style lang="less" scoped>
.entity-edit-form {
  .edit-item-btn {
    color: #fff;
  }

  &.with-floating-buttons {
    padding-bottom: 140px;
  }

  ::v-deep {
    .v-btn {
      border-radius: 6px;
    }
  }
}
</style>
